import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import normalizeResumeData from '../utils/normalizeResumeData';
import { useAuth } from '../hooks/useAuth';
import { useCanvas } from '../hooks/useCanvas';
import { useVariants } from '../hooks/useVariants';
import { useCoverLetters } from '../hooks/useCoverLetters';
import { useAssets } from '../hooks/useAssets';
import { useDownload } from '../hooks/useDownload';
import { useConversationSession } from '../hooks/useConversationSession';
import { useWorkspace } from '../domains/workspace/state/useWorkspace';
import { useAts } from '../hooks/useAts';
import { SUGGESTION_CHIPS, SAMPLE_RESUME_DATA } from '../utils/constants';
import AppEvents from '../utils/appEventBus';
import { createDocument as apiCreateDocument } from '../domains/document/api/documentApi';
import { resolveDocumentId } from '../utils/navigationGuard';

export const AppContext = createContext(null);

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used inside AppContext.Provider');
  return ctx;
};

export const AppContextProvider = ({ children }) => {
  // ── Global Layout / UI / Theme State ──
  const [theme, setTheme] = useState(() => localStorage.getItem('rz_theme') || 'dark');
  const [lightningActive, setLightningActive] = useState(false);
  const [credits, setCredits] = useState(null); // null = loading, number = loaded
  const [plan, setPlan] = useState("free"); // "free" | "starter" | "pro" | "elite"
  const [referralCode, setReferralCode] = useState("");
  const [referredByCode, setReferredByCode] = useState(null);
  const [creditsRetryCount, setCreditsRetryCount] = useState(0);
  const [showPricing, setShowPricing] = useState(false);
  const [userDomain, setUserDomain] = useState(null);
  const [showDomainSelector, setShowDomainSelector] = useState(false);
  const [mobileTab, setMobileTab] = useState(2); // 0=Templates, 1=Chat, 2=Preview
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState("resume");
  const [aiTone, setAiToneState] = useState(() => localStorage.getItem('rz_ai_tone') || 'professional');

  const setAiTone = (tone) => {
    setAiToneState(tone);
    try {
      localStorage.setItem('rz_ai_tone', tone);
    } catch (e) {
      console.warn("localStorage setItem failed:", e);
    }
  };

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingResume, setIsUploadingResume] = useState(false);

  // ── Import Modal / Review Screen State ──
  // showImportModal: shows ImportChoiceModal before file dialog
  // importMode: 'current' | 'new' — set by the modal choice
  // importReviewData: holds parsed resume data awaiting user acceptance
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('new');
  const [importReviewData, setImportReviewData] = useState(null);
  // True when the import was triggered from "Create New" — the uploaded file
  // should become a brand-new document, never overwrite the master resume.
  const [importCreatesNewDocument, setImportCreatesNewDocument] = useState(false);

  // ── Autosave Status ──
  // 'idle' | 'pending' | 'saving' | 'offline' | 'retry' | 'error'
  const [autosaveStatus, setAutosaveStatus] = useState('idle');

  // ── Creation UI State ──
  // UI-only flag: true while a Create Resume / Create Cover Letter operation is in flight.
  // Used ONLY for: disabling buttons, showing skeleton, suppressing autosave.
  // NOT used as business-logic gate in routing or recovery.
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);

  // ── Production Readiness: Library & Progress States ──
  // libraryLoaded: true once the initial /api/document/list call resolves.
  // This must NOT use variants.length===0 to detect loading (empty library is valid).
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  // activeProgress: drives the global RuntimeProgressOverlay.
  // Shape: { title, statusText, message, percent, error, onCancel, onRetry, onClose }
  const [activeProgress, setActiveProgress] = useState(null);

  // Import abort controller ref — allows mid-flight cancellation
  const importAbortControllerRef = useRef(null);

  const photoInputRef = useRef(null);
  const resumeFileInputRef = useRef(null);
  const switchDocumentRef = useRef(null);
  const coverLetterUpdateRef = useRef(null);
  // Stable refs used to break infinite re-render loops
  const sessionRef = useRef(null);           // mirrors session.session without being a dep
  const fetchDocumentsRef = useRef(null);    // mirrors fetchDocuments without being a dep
  const switchDocumentCbRef = useRef(null);  // mirrors switchDocument without being a dep
  const activeCoverLetterRef = useRef(null); // mirrors coverLetters.activeCoverLetter

  // Sync theme attribute on documentElement
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-landing-theme', theme === 'dark' ? 'dark' : 'light');
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('rz_theme', next);
      return next;
    });
  };

  const triggerLightningAnimation = useCallback(() => {
    // Disabled as requested
  }, []);

  // Wrap switchDocument to break circular dependency in hooks
  const switchDocumentWrapper = useCallback((session) => {
    if (switchDocumentRef.current) {
      switchDocumentRef.current(session);
    }
  }, []);

  const [documents, setDocuments] = useState([]);

  // Normalize document metadata from backend representation to canonical schema
  const normalizeDocumentMetadata = useCallback((rawDoc) => {
    if (!rawDoc) return null;
    if (rawDoc.id && rawDoc.type) {
      return {
        id: rawDoc.id,
        type: rawDoc.type === 'resume_variant' ? 'variant' : rawDoc.type,
        name: rawDoc.name,
        isMaster: !!rawDoc.isMaster,
        updatedAt: rawDoc.updatedAt || rawDoc.updated_at || new Date(0).toISOString()
      };
    }
    if (rawDoc.variant_type || rawDoc.overrides) {
      return {
        id: rawDoc.id,
        type: 'variant',
        name: rawDoc.name || 'New Variant',
        isMaster: false,
        updatedAt: rawDoc.updated_at || rawDoc.updatedAt || new Date(0).toISOString()
      };
    }
    if (rawDoc.content !== undefined && rawDoc.company !== undefined) {
      return {
        id: String(rawDoc.id),
        type: 'cover_letter',
        name: rawDoc.company || 'New Cover Letter',
        isMaster: false,
        updatedAt: rawDoc.updated_at || rawDoc.updatedAt || new Date(0).toISOString()
      };
    }
    return null;
  }, []);

  // Backend Mutation Flow merge handler (Rule 2)
  const mergeDocumentMetadata = useCallback((rawMeta) => {
    const meta = normalizeDocumentMetadata(rawMeta);
    if (!meta) return;
    setDocuments((prev) => {
      const exists = prev.some(d => d.id === meta.id);
      if (exists) {
        return prev.map(d => d.id === meta.id ? { ...d, ...meta } : d);
      }
      return [...prev, meta];
    });
  }, [normalizeDocumentMetadata]);

  // 1. Hook: useAuth
  const auth = useAuth();

  // 3. Hook: useConversationSession (The ONLY Source of Truth)
  const session = useConversationSession({
    authToken: auth.authToken,
    triggerLightningAnimation,
    onCoverLetterUpdate: useCallback((cl) => {
      if (coverLetterUpdateRef.current) coverLetterUpdateRef.current(cl);
    }, []),
    onBackendMutation: mergeDocumentMetadata,
    onCreditsUpdate: useCallback((newCredits) => {
      setCredits(newCredits);
    }, []),
  });
  const { loadConversation, setEntryIntent } = session;

  // 2. Hook: useCanvas
  const canvas = useCanvas({ editMode, resumeData: session.activeResume });

  // 4. Hook: useVariants
  const variants = useVariants({
    authToken: auth.authToken,
    switchDocument: switchDocumentWrapper,
    setCredits,
  });

  // 5. Hook: useCoverLetters
  const coverLetters = useCoverLetters({
    authToken: auth.authToken,
    coverTemplate: session.coverTemplate,
    setCoverTemplate: session.setCoverTemplate,
    setActiveTab: () => {},
  });

  // Assign cover letter update ref (breaks circular dep: session created before coverLetters)
  coverLetterUpdateRef.current = coverLetters.updateCoverLetterState;

  // 6. Hook: useAssets
  const assets = useAssets({
    authToken: auth.authToken,
    setToast: auth.setToast,
  });

  // Refs for chat input/scrolling compatibility
  const replyEndRef = useRef(null);
  const textareaRef = useRef(null);

  // 6.5 Hook: useAts
  const ats = useAts({
    authToken: auth.authToken,
    resumeData: session.activeResume,
    setResumeData: session.setActiveResume,
    getAuthHeaders: auth.getAuthHeaders,
    credits,
    setCredits,
    setShowPricing,
    setReply: session.setConversation,
    replyEndRef,
    triggerLightningAnimation,
    setToast: auth.setToast,
  });

  // 7. Hook: useDownload
  const download = useDownload({
    documentId: session.session?.document_id || 'master',
    resumeData: session.activeResume,
    template: session.template,
    accentColor: session.accentColor,
    editMode,
    setEditMode,
    fontFamily: session.fontFamily,
    fontScale: session.fontScale,
    getAuthHeaders: auth.getAuthHeaders,
  });

  const { setActiveCoverLetter } = coverLetters;
  const { setActiveVariantId } = variants;

  // Unified Document/Tab switcher
  const switchDocument = useCallback(async (docSession, changeTab = true) => {
    setEditMode(false);

    const isCover = docSession.type === 'cover_letter';
    const requestedId = isCover ? docSession.id : (docSession.id || 'master');
    const isMaster = !isCover && (docSession.type === 'master' || requestedId === 'master' || !!docSession.isMaster);
    const requestedType = isCover ? 'cover_letter' : (isMaster ? 'master' : 'variant');

    // Read current session from ref (stable — does NOT cause this callback to recreate)
    const currentSession = sessionRef.current;
    const currentId = currentSession?.document_id || 'master';
    const currentTypeRaw = currentSession?.type || 'master';
    const currentIsMaster = currentId === 'master' || currentTypeRaw === 'master';
    const currentType = currentTypeRaw === 'cover_letter'
      ? 'cover_letter'
      : (currentIsMaster ? 'master' : 'variant');

    // Prevent redundant switches if the correct document is already active
    if (currentSession && String(currentId) === String(requestedId) && String(currentType) === String(requestedType)) {
      console.log("[switchDocument] Document already active, skipping switch.");
      return;
    }

    if (isCover) {
      const coverData = docSession.document_data || { id: docSession.id };
      setActiveCoverLetter(coverData);
      setActiveVariantId(null);
      console.log(`[TRACE:switch] cover_letter -> activeVariantId=null, activeTab=cover`);
    } else {
      const isVariant = requestedType === 'variant';
      setActiveVariantId(isVariant ? docSession.id : null);
      setActiveCoverLetter(null);
      console.log(`[TRACE:switch] type=${docSession.type} isVariant=${isVariant} -> activeVariantId=${isVariant ? docSession.id : 'null'}`);
      console.log(`[TRACE:switch] NOTE: if isVariant=true, ResumeCanvas will render resumeData`);
    }

    // Load conversation history, active resume, and ATS score in one unified action
    const docId = docSession.id || 'master';
    await loadConversation(docSession.type, docId, isMaster);
  }, [
    loadConversation,
    setActiveCoverLetter,
    setActiveVariantId,
  ]);

  // Assign the switchDocument back to ref so hooks can resolve dependencies
  switchDocumentRef.current = switchDocument;

  // 8. Hook: useWorkspace & Documents lifecycle states
  const { activeWorkspace, setActiveWorkspace, workspaces, createWorkspace } = useWorkspace();

  // Memoized projections for Resume Library and Cover Letters (Invariants 1 & 2)
  const resumeLibraryItems = React.useMemo(() => {
    const items = documents.filter(doc => ['resume', 'variant'].includes(doc.type));
    return items.sort((a, b) => {
      if (a.isMaster) return -1;
      if (b.isMaster) return 1;
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });
  }, [documents]);

  const coverLetterItems = React.useMemo(() => {
    return documents
      .filter(doc => doc.type === 'cover_letter')
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [documents]);

  // Fetch unified document index from GET /api/document/list
  const fetchDocuments = useCallback(async (workspaceId = null) => {
    if (!auth?.authToken) return;
    try {
      const url = workspaceId 
        ? `/api/document/list?workspace_id=${workspaceId}` 
        : '/api/document/list';
      const res = await auth.apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        const normalized = (data || []).map(normalizeDocumentMetadata).filter(Boolean);
        setDocuments(normalized);
      }
    } catch (err) {
      console.error("[fetchDocuments] Error:", err);
    } finally {
      // Mark library as loaded regardless of success/failure.
      // This prevents the skeleton from hanging when the user has zero documents.
      setLibraryLoaded(true);
    }
  }, [auth?.authToken, auth?.apiFetch, normalizeDocumentMetadata]);

  // Create cover letter callback mapping
  const handleCreateCoverLetter = useCallback(async () => {
    try {
      const res = await auth.apiFetch("/api/cover-letter", {
        method: "POST",
        body: JSON.stringify({
          company: "",
          job_title: "",
          template: "classic",
        })
      });
      if (res.ok) {
        const newCl = await res.json();
        mergeDocumentMetadata(newCl);
        switchDocument({ type: 'cover_letter', id: newCl.id, document_data: newCl });
        return newCl;
      }
    } catch (err) {
      console.error("Error creating cover letter:", err);
    }
    return null;
  }, [auth.apiFetch, mergeDocumentMetadata, switchDocument]);

  // Create variant callback mapping
  const handleCreateVariant = useCallback(async () => {
    try {
      const res = await auth.apiFetch("/api/variants", {
        method: "POST",
        body: JSON.stringify({
          name: `Variant — ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
          variant_type: "document",
          overrides: {
            name: "",
            email: "",
            phone: "",
            city: "",
            role: "",
            summary: "",
            skills: [],
            experience: [],
            education: [],
            certifications: [],
            extracurricular: [],
            achievements: [],
            links: {},
          }
        })
      });
      if (res.ok) {
        const data = await res.json();
        mergeDocumentMetadata(data.variant);
        switchDocument({ type: 'variant', id: data.variant.id });
        return data.variant;
      }
    } catch (err) {
      console.error("Error creating variant:", err);
    }
    return null;
  }, [auth.apiFetch, mergeDocumentMetadata, switchDocument]);

  // Delete document callback mapping
  const handleDeleteDocument = useCallback(async (docId, type) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return false;
    try {
      const url = type === 'cover_letter' && !isNaN(Number(docId))
        ? `/api/cover-letter/${docId}`
        : `/api/document/${docId}`;
      const res = await auth.apiFetch(url, {
        method: "DELETE"
      });
      if (res.ok) {
        setDocuments((prev) => prev.filter(d => d.id !== docId));
        const activeId = localStorage.getItem("rz_active_doc_id");
        if (activeId === docId) {
          const masterResume = resumeLibraryItems.find(item => item.isMaster);
          if (masterResume) {
            switchDocument(masterResume);
          } else {
            switchDocument({ type: 'resume', id: 'master' });
          }
        }
        return true;
      }
    } catch (err) {
      console.error("[handleDeleteDocument] Error:", err);
    }
    return false;
  }, [auth.apiFetch, resumeLibraryItems, switchDocument]);

  // Rename document callback mapping
  const handleRenameDocument = useCallback(async (docId, newName) => {
    try {
      const isLegacyCL = !isNaN(Number(docId));
      const url = isLegacyCL ? `/api/cover-letter/${docId}` : `/api/document/${docId}`;
      const method = isLegacyCL ? "PUT" : "PATCH";
      const body = isLegacyCL 
        ? JSON.stringify({ company: newName, job_title: "" })
        : JSON.stringify({ name: newName });

      const res = await auth.apiFetch(url, {
        method,
        body
      });
      if (res.ok) {
        const data = await res.json();
        if (isLegacyCL) {
          mergeDocumentMetadata(data);
        } else {
          mergeDocumentMetadata(data.metadata);
        }
      }
    } catch (err) {
      console.error("[handleRenameDocument] Error:", err);
    }
  }, [auth.apiFetch, mergeDocumentMetadata]);

  // Duplicate document callback mapping
  const handleDuplicateDocument = useCallback(async (docId, type) => {
    try {
      let url = '';
      if (type === 'variant') {
        url = `/api/variants/${docId}/duplicate`;
      } else if (type === 'cover_letter') {
        url = `/api/cover-letter/${docId}/duplicate`;
      } else {
        url = `/api/document/${docId}/duplicate`;
      }
      const res = await auth.apiFetch(url, {
        method: "POST"
      });
      if (res.ok) {
        const data = await res.json();
        const dupMeta = data.metadata || data.variant;
        mergeDocumentMetadata(dupMeta);
        switchDocument({ type: dupMeta.type || type, id: dupMeta.id });
        return dupMeta;
      }
    } catch (err) {
      console.error("[handleDuplicateDocument] Error:", err);
    }
    return null;
  }, [auth.apiFetch, mergeDocumentMetadata, switchDocument]);

  // Workspace Switch Lifecycle: clear documents and reset libraryLoaded upon switching workspaces
  useEffect(() => {
    if (activeWorkspace?.id) {
      setDocuments([]);
      setLibraryLoaded(false);
    }
  }, [activeWorkspace?.id]);

  // Keep refs up-to-date on every render (no re-renders caused by these assignments)
  sessionRef.current = session.session;
  fetchDocumentsRef.current = fetchDocuments;
  switchDocumentCbRef.current = switchDocument;

  // App Bootstrap & Workspace Switch Lifecycle Effect
  // FIX: Use stable refs for fetchDocuments and switchDocument so this effect only
  // runs when authToken or workspaceId actually changes — NOT on every callback recreation.
  useEffect(() => {
    if (!auth.authToken) return;
    // Call via ref so this effect has no dependency on the callback itself
    fetchDocumentsRef.current(activeWorkspace?.id || null).then(() => {
      // Only switchDocument if we are NOT on a deep-linked document route
      const path = window.location.pathname;
      const isDeepLink = path.includes('/app/resume/') || path.includes('/app/cover-letter/');
      if (!isDeepLink) {
        const savedType = localStorage.getItem("rz_active_doc_type");
        const savedId = localStorage.getItem("rz_active_doc_id");
        if (savedType && savedId) {
          switchDocumentCbRef.current({ type: savedType, id: savedId }, false);
        } else {
          switchDocumentCbRef.current({ type: 'resume', id: 'master' }, false);
        }
      }
    });
  }, [auth.authToken, activeWorkspace?.id]); // REMOVED fetchDocuments — was causing infinite loop

  // Credits fetcher
  useEffect(() => {
    if (!auth.authToken) return;
    let cancelled = false;
    let retryTimer = null;

    const fetchCredits = async (attempt = 0) => {
      if (cancelled) return;
      try {
        const res = await auth.apiFetch('/api/payments/credits');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setCredits(data.credits ?? 0);
          setPlan(data.plan ?? "free");
          setReferralCode(data.referral_code ?? "");
          setReferredByCode(data.referred_by_code ?? null);
        } else if (res.status === 401) {
          console.warn('[auth] JWT expired detected on /api/payments/credits — logged out');
        } else if (attempt < 3) {
          retryTimer = setTimeout(() => fetchCredits(attempt + 1), 3000);
        } else {
          setCredits(-1);
        }
      } catch {
        if (cancelled) return;
        if (attempt < 3) {
          retryTimer = setTimeout(() => fetchCredits(attempt + 1), 3000);
        } else {
          setCredits(-1);
        }
      }
    };
    fetchCredits();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [auth.authToken, creditsRetryCount, auth.apiFetch]);



  // Photo & Resume upload triggers
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("Photo must be less than 2MB.");
      return;
    }

    setIsUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = auth.authToken || localStorage.getItem('auth_token');
      const res = await fetch("/api/resume/photo", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();

      if (res.ok) {
        session.setActiveResume(normalizeResumeData(data.resume_data));
        session.setConversation((prev) => [
          ...prev,
          { role: "assistant", content: "📷 Profile photo uploaded successfully!" },
        ]);
      } else {
        alert(`Upload failed: ${data.error}`);
      }
    } catch (err) {
      alert("Network error during photo upload.");
    } finally {
      setIsUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  /**
   * handleResumeUpload
   *
   * Production-grade resume import pipeline:
   *   1. Validate file type & size
   *   2. Show polished 7-stage progress overlay
   *   3. Use AbortController for mid-flight cancellation
   *   4. Enforce 30-second timeout (NETWORK_TIMEOUT)
   *   5. Retry up to 3x with exponential backoff on network failures
   *   6. On success: show ImportReviewScreen before committing data
   *   7. Review screen: Accept → commit + open AI chat with dynamic welcome
   *                     Edit   → commit + open editor
   *                     Discard → cancel import entirely
   *   8. On failure: show error in overlay with Retry option
   */
  const handleResumeUpload = async (fileOrEvent, onSuccess = null) => {
    const file = fileOrEvent instanceof File ? fileOrEvent : fileOrEvent?.target?.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) {
      setActiveProgress({
        title: 'Unsupported File',
        error: 'Only PDF and DOCX files are supported.',
        onClose: () => setActiveProgress(null),
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setActiveProgress({
        title: 'File Too Large',
        error: 'File must be less than 5MB.',
        onClose: () => setActiveProgress(null),
      });
      return;
    }
    if (credits !== null && credits < 5) {
      setShowPricing(true);
      if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
      return;
    }

    // ── Import Milestones ────────────────────────────────────────────────────
    const STAGES = [
      { text: 'Uploading your resume...',                  pct: 5  },
      { text: 'Parsing document structure...',             pct: 15 },
      { text: 'Extracting contact & skills...',            pct: 30 },
      { text: 'Extracting work history...',                pct: 48 },
      { text: 'Extracting education & projects...',        pct: 63 },
      { text: 'Building your Career Vault...',             pct: 75 },
      { text: 'Scoring your ATS profile...',               pct: 85 },
      { text: 'Finalizing your career graph...',           pct: 94 },
      { text: 'Almost there...',                           pct: 99 },
    ];

    let stageIndex = 0;
    let stageTimer = null;
    let cancelled = false;

    const abortController = new AbortController();
    importAbortControllerRef.current = abortController;

    const advanceStage = () => {
      stageIndex = Math.min(stageIndex + 1, STAGES.length - 1);
      setActiveProgress(prev => prev && !prev.error ? {
        ...prev,
        statusText: STAGES[stageIndex].text,
        percent: STAGES[stageIndex].pct,
      } : prev);
      if (stageIndex < STAGES.length - 1) {
        // Spread 9 stages across ~180s so bar moves visibly throughout the wait
        stageTimer = setTimeout(advanceStage, 2000 + stageIndex * 1800);
      }
    };

    // Show overlay immediately
    setIsUploadingResume(true);
    setActiveProgress({
      title: 'Importing Resume',
      statusText: STAGES[0].text,
      percent: STAGES[0].pct,
      error: null,
      onCancel: () => {
        cancelled = true;
        abortController.abort();
        clearTimeout(stageTimer);
        setActiveProgress(null);
        setIsUploadingResume(false);
        if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
      },
    });

    // Start stage advancement
    stageTimer = setTimeout(advanceStage, 2500);

    // 240-second timeout — 3 LLM calls run in parallel on the backend (~30-40s each).
    // Sequential was up to 3×120s = 360s. Parallel cuts it to ~40s, but we keep
    // 240s as headroom for slow OpenRouter days.
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        abortController.abort();
        clearTimeout(stageTimer);
        setActiveProgress({
          title: 'Import Timed Out',
          error: 'The AI is taking longer than expected. This usually happens when the server is busy. Please try again in a moment.',
          onRetry: () => handleResumeUpload(fileOrEvent, onSuccess),
          onClose: () => setActiveProgress(null),
        });
        setIsUploadingResume(false);
      }
    }, 240000);

    const attemptUpload = async (attempt = 1) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch('/api/resume/upload', {
          method: 'POST',
          headers: auth.getAuthHeaders(),
          body: formData,
          signal: abortController.signal,
        });
        const data = await res.json();

        if (res.ok && data.ok) {
          clearTimeout(timeoutId);
          clearTimeout(stageTimer);
          const normalized = normalizeResumeData(data.resume_data);
          if (typeof data.credits_remaining === 'number') {
            setCredits(data.credits_remaining);
          } else {
            setCredits(prev => Math.max(0, (prev ?? 0) - 5));
          }

          // Final "done" progress before showing review screen
          setActiveProgress(prev => prev && !prev.error ? {
            ...prev,
            statusText: 'Parsing complete — review your data',
            percent: 100,
          } : prev);
          setTimeout(() => setActiveProgress(null), 600);

          if (onSuccess) {
            // Legacy callback path — skip review screen
            onSuccess(normalized);
          } else {
            // Show ImportReviewScreen — user must accept/edit/discard
            setImportReviewData(normalized);
            // importMode was set by ImportChoiceModal before file dialog
          }

          // Emit event for decoupled listeners (sidebar, dashboard, etc.)
          AppEvents.emit('ImportFinished', { fileName: file.name });
          await fetchDocuments(activeWorkspace?.id || null);

        } else if (!cancelled && attempt < 3) {
          // Retry on server error (non-abort)
          const delay = 1000 * Math.pow(2, attempt - 1);
          console.warn(`[handleResumeUpload] Attempt ${attempt} failed. Retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return attemptUpload(attempt + 1);
        } else if (!cancelled) {
          throw new Error(data.error || 'Upload failed after multiple attempts.');
        }
      } catch (err) {
        clearTimeout(timeoutId);
        clearTimeout(stageTimer);
        if (cancelled || err.name === 'AbortError') return;

        if (attempt < 3) {
          const delay = 1000 * Math.pow(2, attempt - 1);
          console.warn(`[handleResumeUpload] Attempt ${attempt} network error. Retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return attemptUpload(attempt + 1);
        }

        AppEvents.emit('ImportFailed', { fileName: file.name, error: err.message });
        setActiveProgress({
          title: 'Import Failed',
          error: err.message || 'An unexpected error occurred. Please try again.',
          onRetry: () => {
            setActiveProgress(null);
            // Re-trigger from scratch on retry
            setTimeout(() => handleResumeUpload(fileOrEvent, onSuccess), 100);
          },
          onClose: () => setActiveProgress(null),
        });
      } finally {
        if (!cancelled) {
          setIsUploadingResume(false);
          if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
        }
      }
    };

    await attemptUpload(1);
  };

  /**
   * handleImportAccept
   * Called when user clicks "Accept & Open AI Chat" in ImportReviewScreen.
   * Commits parsed resume data, then reloads the session with an entry_intent
   * that tells the BACKEND to generate the greeting server-side (using the
   * real, just-saved resume state) — never a hand-built prompt sent as a fake
   * user message. importMode='new' forces a fresh thread; 'current' appends
   * a short acknowledgment to the existing one.
   */
  const handleImportAccept = useCallback(async (parsed) => {
    if (!parsed) return null;
    setImportReviewData(null);

    if (!parsed._domain) {
      setShowDomainSelector(true);
    }

    // "Continue in current conversation" — regardless of which button opened
    // the upload flow (sidebar, chat workspace, or Create New), this choice
    // means attach the uploaded data to whatever document/chat is ALREADY
    // open right now. Never create a new document, never force it onto master.
    if (importMode === 'current') {
      setImportCreatesNewDocument(false);
      const activeType = session.session?.type || 'master';
      const activeId    = session.session?.document_id || 'master';
      const isMaster    = activeType === 'master' || activeId === 'master';

      try {
        sessionStorage.setItem('mmr_pending_entry_intent', 'resume_imported');
      } catch (_) {}

      try {
        await session.loadConversation(activeType, activeId, isMaster);
      } catch (_) {}

      session.setActiveResume(parsed);
      return activeId;
    }

    // "Create New" origin: the uploaded file is meant to become a brand-new
    // document, not overwrite the master resume. Create a fresh workspace +
    // document from the parsed data and open a new chat scoped to it.
    if (importCreatesNewDocument) {
      setImportCreatesNewDocument(false);
      try {
        const finalName = parsed?.name ? `${parsed.name}'s Resume` : 'Imported Resume';
        const newWs = await createWorkspace({ name: `Workspace - ${finalName}` });
        const result = await apiCreateDocument(newWs.id, 'resume', finalName, { content: parsed });
        const docId = resolveDocumentId(result);

        mergeDocumentMetadata({
          id: docId,
          type: 'resume',
          name: finalName,
          isMaster: false,
          updatedAt: new Date().toISOString(),
          ...(result?.metadata || {}),
        });

        if (setEntryIntent) setEntryIntent(importMode === 'new' ? 'resume_imported_new' : 'resume_imported');

        await switchDocument({ type: 'resume', id: docId });
        session.setActiveResume(parsed);
        fetchDocuments(activeWorkspace?.id || null).catch(() => {});
        return docId;
      } catch (err) {
        console.error('[handleImportAccept] new-document import failed:', err);
        setActiveProgress({
          title: 'Import Failed',
          error: err.message || 'Failed to create a new resume from the uploaded file.',
          onClose: () => setActiveProgress(null),
        });
        return null;
      }
    }

    // Resume imports targeting the existing master resume — never a variant.
    // Using the current session's document_id here was the root cause: when
    // the user had a variant open (e.g. doc_resume_73900fccfb7d), the import
    // was associated with that variant's session instead of the master session.
    const docType  = 'resume';
    const docId    = 'master';
    const isMaster = true;

    try {
      sessionStorage.setItem(
        'mmr_pending_entry_intent',
        importMode === 'new' ? 'resume_imported_new' : 'resume_imported'
      );
    } catch (_) {}

    try {
      await session.loadConversation(docType, docId, isMaster);
    } catch (_) {}

    // Commit resume data AFTER session is loaded so state updates don't
    // trigger re-renders that race with the session context fetch above.
    session.setActiveResume(parsed);
    return 'master';
  }, [
    importMode,
    importCreatesNewDocument,
    session,
    setShowDomainSelector,
    createWorkspace,
    mergeDocumentMetadata,
    setEntryIntent,
    switchDocument,
    fetchDocuments,
    activeWorkspace,
  ]);


  /**
   * handleImportEdit
   * Called when user clicks "Edit before importing" in ImportReviewScreen.
   * Commits data but routes to the editor tab instead of chat.
   */
  const handleImportEdit = useCallback((parsed) => {
    if (!parsed) return;
    setImportReviewData(null);
    session.setActiveResume(parsed);
    // Signal to navigate to editor — consumers listen via AppEvents
    AppEvents.emit('ImportEdit', { mode: 'editor' });
  }, [session]);

  /**
   * handleImportDiscard
   * Called when user clicks "Discard import" in ImportReviewScreen.
   */
  const handleImportDiscard = useCallback(() => {
    setImportReviewData(null);
    setImportCreatesNewDocument(false);
  }, []);

  /**
   * triggerImportModal
   * Call this instead of directly triggering resumeFileInputRef.
   * Shows the ImportChoiceModal first.
   * @param {boolean} createNew - true when triggered from "Create New": the
   *   uploaded file becomes a brand-new document instead of updating master.
   */
  const triggerImportModal = useCallback((createNew = false) => {
    setImportCreatesNewDocument(createNew);
    setShowImportModal(true);
  }, []);

  /**
   * handleImportModalConfirm
   * Called when user confirms choice in ImportChoiceModal.
   * Stores importMode and opens the file dialog.
   */
  const handleImportModalConfirm = useCallback((mode) => {
    setImportMode(mode);
    setShowImportModal(false);
    // Small delay so modal can animate out before OS dialog appears
    setTimeout(() => {
      if (resumeFileInputRef.current) {
        resumeFileInputRef.current.click();
      }
    }, 150);
  }, []);

  const maxInputChars = plan === "starter" ? 10000 : plan === "pro" ? 30000 : plan === "elite" ? 70000 : 1500;

  // Keep activeCoverLetter ref up-to-date (written each render, never causes re-renders)
  activeCoverLetterRef.current = coverLetters.activeCoverLetter;

  // Wrapped API fetch to inject active document context automatically into payload.
  // FIX: coverLetters.activeCoverLetter removed from deps — read via ref instead.
  // activeCoverLetter is an object that changes reference on every render, which was
  // causing apiFetch → fetchDocuments → useEffect to refire continuously.
  const apiFetch = useCallback(async (url, options = {}) => {
    let newOptions = { ...options };
    if (options.method === 'POST' || options.method === 'PUT') {
      try {
        if (options.body && typeof options.body === 'string') {
          const parsed = JSON.parse(options.body);
          if (!parsed.active_document) {
            let activeDoc = null;
            if (variants.activeVariantId) {
              activeDoc = { type: 'resume_variant', id: variants.activeVariantId };
            } else if (activeCoverLetterRef.current) {
              activeDoc = { type: 'cover_letter', id: activeCoverLetterRef.current.id };
            } else {
              activeDoc = { type: 'master_resume', id: 'master' };
            }
            parsed.active_document = activeDoc;
            newOptions.body = JSON.stringify(parsed);
          }
        }
      } catch (e) {}
    }
    return auth.apiFetch(url, newOptions);
  // REMOVED: coverLetters.activeCoverLetter — now read via ref to prevent infinite loop
  }, [auth.apiFetch, variants.activeVariantId]);

  // Wrapped getAuthHeaders
  const getAuthHeaders = useCallback(() => {
    return auth.getAuthHeaders();
  }, [auth.getAuthHeaders]);

  // Compatibility UI states
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const [input, setInput] = useState('');
  const [showJdModal, setShowJdModal] = useState(false);
  const [showJdPanel, setShowJdPanel] = useState(false);

  const [aiDebugEnabled, setAiDebugEnabled] = useState(() => localStorage.getItem('ai_debug') === 'true');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        setAiDebugEnabled(prev => {
          const next = !prev;
          localStorage.setItem('ai_debug', next ? 'true' : 'false');
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Redundant session-restore removed in favor of App Bootstrap effect with deep-link guards

  // Debounced style state sync hook
  useEffect(() => {
    if (!auth.authToken) return;
    if (!session.activeResume) return;

    const syncStylePreferences = async () => {
      try {
        const payload = {
          action: "update_style",
          payload: {
            theme: {
              template: session.template,
              template_version: session.templateVersion || "2.2.0",
              accent_color: session.accentColor,
              font: {
                family: session.fontFamily,
                scale: session.fontScale
              },
              spacing: {}
            }
          }
        };
        await apiFetch('/api/workspace/document', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.warn('[style-sync] Failed to sync styles:', err);
      }
    };

    const timer = setTimeout(syncStylePreferences, 500);
    return () => clearTimeout(timer);
  }, [session.template, session.templateVersion, session.accentColor, session.fontFamily, session.fontScale, auth.authToken, apiFetch, session.activeResume]);

  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastSectionSavedRef = useRef("changes");

  const pushUndoState = useCallback((sectionName) => {
    if (session.activeResume) {
      undoStackRef.current.push(JSON.parse(JSON.stringify(session.activeResume)));
      redoStackRef.current = []; // Clear redo stack on new action
      lastSectionSavedRef.current = sectionName || "changes";
    }
  }, [session.activeResume]);

  const dispatchUndoableOperation = useCallback(async (sectionName, operation) => {
    pushUndoState(sectionName);
    return session.dispatchOperation(operation);
  }, [pushUndoState, session]);

  const handleUndo = useCallback(async () => {
    if (undoStackRef.current.length === 0) return;
    const previousState = undoStackRef.current.pop();
    if (session.activeResume) {
      redoStackRef.current.push(JSON.parse(JSON.stringify(session.activeResume)));
    }
    session.setActiveResume(previousState);
    await session.dispatchOperation({ type: "RESTORE_STATE", value: previousState });
    auth.setToast({ message: `Undid ${lastSectionSavedRef.current || 'Resume'} changes` });
  }, [session, auth]);

  const handleRedo = useCallback(async () => {
    if (redoStackRef.current.length === 0) return;
    const nextState = redoStackRef.current.pop();
    if (session.activeResume) {
      undoStackRef.current.push(JSON.parse(JSON.stringify(session.activeResume)));
    }
    session.setActiveResume(nextState);
    await session.dispatchOperation({ type: "RESTORE_STATE", value: nextState });
    auth.setToast({ message: `Redid ${lastSectionSavedRef.current || 'Resume'} changes` });
  }, [session, auth]);

  const claimReferralCode = async (code) => {
    try {
      const res = await auth.apiFetch('/api/credits/claim-referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCredits(data.credits);
        setReferredByCode(data.referred_by_code);
        return { success: true, message: data.message };
      }
      return { success: false, error: data.error || 'Failed to claim referral code.' };
    } catch (err) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Compile full context
  const ctxValue = {
    authToken: auth.authToken,
    authInitializing: auth.initializing,
    setAuthToken: auth.setAuthToken,
    user: auth.user,
    setUser: auth.setUser,
    authError: auth.authError,
    setAuthError: auth.setAuthError,
    handleLogout: auth.handleLogout,
    credits,
    setCredits,
    plan,
    setPlan,
    referralCode,
    referredByCode,
    claimReferralCode,
    showPricing,
    setShowPricing,
    assets: assets.assets,
    setAssets: assets.setAssets,
    fetchAssets: assets.fetchAssets,
    uploadAsset: assets.uploadAsset,
    deleteAsset: assets.deleteAsset,
    isUploadingAsset: assets.isUploadingAsset,
    qualityScore: 0,
    setQualityScore: () => {},
    fetchQualityScore: () => {},
    isFetchingQuality: false,
    careerMemory: {},
    setCareerMemory: () => {},
    fetchCareerMemory: () => {},
    updateCareerMemory: () => {},
    resumeData: session.activeResume,
    setResumeData: session.setActiveResume,
    // ── Production Readiness: new states ──
    libraryLoaded,
    activeProgress,
    setActiveProgress,
    template: session.template,
    setTemplate: session.setTemplate,
    templateVersion: session.templateVersion,
    setTemplateVersion: session.setTemplateVersion,
    accentColor: session.accentColor,
    setAccentColor: session.setAccentColor,
    fontScale: session.fontScale,
    setFontScale: session.setFontScale,
    fontFamily: session.fontFamily,
    setFontFamily: session.setFontFamily,
    coverTemplate: session.coverTemplate,
    setCoverTemplate: session.setCoverTemplate,
    coverLetterReloadTrigger: coverLetters.coverLetterReloadTrigger,
    setCoverLetterReloadTrigger: coverLetters.setCoverLetterReloadTrigger,
    activeTab: "home",
    setActiveTab: () => {},
    reply: session.conversation,
    setReply: session.setConversation,
    chatStatus: session.chatStatus,
    input,
    setInput,
    isStreaming: session.isStreaming,
    streamingText: session.streamingText,
    chatSteps: [],
    shouldFlash: false,
    aiProvider: "openai",
    // Phase 3 Intelligence Core
    conversationMode: session.conversationMode,
    completeness: session.completeness,
    opportunities: session.opportunities,
    mutationFeedback: session.mutationFeedback,
    templateSuggestion: null,
    setTemplateSuggestion: () => {},
    currentSessionId: session.session?.session_id,
    setCurrentSessionId: (val) => session.setSession(prev => ({ ...prev, session_id: val })),
    errorBanner: auth.errorBanner,
    setErrorBanner: auth.setErrorBanner,
    showHistorySidebar,
    setShowHistorySidebar,
    toast: auth.toast,
    setToast: auth.setToast,
    editMode,
    setEditMode,
    transitioning: false,
    mobileTab,
    setMobileTab,
    showMobileActions,
    setShowMobileActions,
    rightPanelTab,
    setRightPanelTab,
    lightningActive,
    theme,
    toggleTheme,
    resumeScale: canvas.resumeScale,
    manualZoom: canvas.manualZoom,
    setManualZoom: canvas.setManualZoom,
    panOffset: canvas.panOffset,
    setPanOffset: canvas.setPanOffset,
    resumeAreaRef: canvas.resumeAreaRef,
    resumeInnerRef: canvas.resumeInnerRef,
    effectiveZoom: canvas.effectiveZoom,
    maxInputChars,
    replyEndRef,
    textareaRef,
    abortRef: { current: null },
    photoInputRef,
    resumeFileInputRef,
    handleUndo,
    handleRedo,
    canUndo: undoStackRef.current.length > 0,
    canRedo: redoStackRef.current.length > 0,
    isUploadingPhoto,
    isUploadingResume,
    isDownloading: download.isDownloading,
    atsScore: ats.atsScore || session.atsScore,
    atsExpanded: ats.atsExpanded,
    setAtsExpanded: ats.setAtsExpanded,
    variants: resumeLibraryItems,
    setVariants: setDocuments,
    activeVariantId: variants.activeVariantId,
    activeCoverLetter: coverLetters.activeCoverLetter,
    setActiveCoverLetter: coverLetters.setActiveCoverLetter,
    coverLetters: coverLetterItems,
    setCoverLetters: setDocuments,
    handleDeleteCoverLetter: (id) => handleDeleteDocument(id, 'cover_letter'),
    handleCreateCoverLetter: handleCreateCoverLetter,
    handleCreateVariant: handleCreateVariant,
    handleRenameVariant: handleRenameDocument,
    handleDuplicateVariant: (id) => handleDuplicateDocument(id, 'variant'),
    handleRenameCoverLetter: handleRenameDocument,
    handleDuplicateCoverLetter: (id) => handleDuplicateDocument(id, 'cover_letter'),
    fetchCoverLetters: () => fetchDocuments(activeWorkspace?.id || null),
    activeWorkspace,
    setActiveWorkspace,
    workspaces,
    documents,
    mergeDocumentMetadata,
    handleDeleteDocument,
    showJdModal,
    setShowJdModal,
    showJdPanel,
    setShowJdPanel,
    isBoosting: ats.isBoosting,
    boostReport: ats.boostReport,
    setBoostReport: ats.setBoostReport,
    boostProgress: ats.boostProgress,
    showDomainSelector,
    setShowDomainSelector,
    userDomain,
    setUserDomain,
    handleSend: async (msg) => {
      pushUndoState("AI rewrite");
      return session.sendMessage(msg);
    },
    setEntryIntent,             // Set entry_intent before chat navigation (used by Dashboard cards, CreateMenu)
    handleDownload: download.handleDownload,
    handleDownloadDocx: download.handleDownloadDocx,
    handleTemplateChange: async (val) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[PIPE:template S2:handleTemplateChange] val=${val}`);
      }
      // Update local state immediately so preview responds without waiting for backend
      session.setTemplate(val);

      // If there are unsaved editor changes in the DOM state or autosave state, save them to the backend first
      // to prevent the CHANGE_TEMPLATE operation payload from overwriting with stale db copy
      const editorIsDirty = autosaveStatus === 'dirty' ||
                            autosaveStatus === 'saving' ||
                            autosaveStatus === 'pending' ||
                            document.querySelector('[style*="var(--status-warning)"]') ||
                            document.querySelector('[class*="AutosaveIndicator"]');

      if (editorIsDirty && session.activeResume) {
        try {
          const docId = session.session?.document_id || 'master';
          const docType = (docId && docId !== 'master') ? 'variant' : 'master';
          const token = auth.authToken || localStorage.getItem('auth_token');

          await fetch('/api/workspace/document', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              action: 'save_resume',
              payload: session.activeResume,
              active_document: {
                id: docId,
                type: docType
              }
            })
          });
        } catch (err) {
          console.error('[handleTemplateChange] Failed to force sync editor data before template change:', err);
        }
      }

      session.dispatchOperation({ type: "CHANGE_TEMPLATE", value: val });
    },
    handleFieldUpdate: (path, val) => dispatchUndoableOperation(path.split(".")[0], { type: "UPDATE_FIELD", path, value: val }),
    handleDeleteBullet: (section, companyName, keyword) => dispatchUndoableOperation(section, { type: "DELETE_BULLET", section, companyName, keyword }),
    handleDeleteSkill: (skill) => dispatchUndoableOperation("Skills", { type: "REMOVE_SKILL", value: skill }),
    handleReorderSections: (order) => dispatchUndoableOperation("Sections order", { type: "REORDER_SECTIONS", value: order }),
    handleClearChat: async () => {
      if (!auth.authToken || !session.session?.session_id) {
        session.setConversation([]);
        return;
      }
      try {
        const token = auth.authToken || localStorage.getItem('auth_token');
        await fetch(`/api/chat/sessions/${session.session.session_id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        const isMaster = session.session.type === 'master';
        const docId = session.session.document_id || 'master';
        await session.loadConversation(session.session.type, docId, isMaster);
      } catch (err) {
        console.error("[clear-chat] Failed to clear session:", err);
        session.setConversation([]);
      }
    },
    handleRetry: () => {
      const lastUser = [...session.conversation].reverse().find(m => m.role === 'user');
      if (lastUser) {
        session.sendMessage(lastUser.content);
      }
    },
    handleAIBoost: ats.handleAIBoost,
    handleAIBoostConfirmed: ats.handleAIBoostConfirmed,
    preBoostScore: ats.preBoostScore,
    postBoostScore: ats.postBoostScore,
    preBoostResume: ats.preBoostResume,
    handleAcceptBoost: ats.handleAcceptBoost,
    handleRejectBoost: ats.handleRejectBoost,
    handlePhotoUpload,
    handleResumeUpload,
    handleSelectVariant: variants.handleSelectVariant,
    switchDocument,
    handleDeleteVariant: (id) => handleDeleteDocument(id, 'variant'),
    handleVariantCreated: variants.handleVariantCreated,
    triggerLightningAnimation,
    fetchVariants: () => fetchDocuments(activeWorkspace?.id || null),
    fetchDocuments,
    apiFetch,
    getAuthHeaders,
    handleCanvasMouseDown: canvas.handleCanvasMouseDown,
    handleCanvasMouseMove: canvas.handleCanvasMouseMove,
    handleCanvasMouseUp: canvas.handleCanvasMouseUp,
    handleAddExperience: () => dispatchUndoableOperation("Experience", { type: "ADD_EXPERIENCE" }),
    handleDeleteExperience: (id) => dispatchUndoableOperation("Experience", { type: "DELETE_EXPERIENCE", value: id }),
    handleAddEducation: () => dispatchUndoableOperation("Education", { type: "ADD_EDUCATION" }),
    handleDeleteEducation: (id) => dispatchUndoableOperation("Education", { type: "DELETE_EDUCATION", value: id }),
    handleAddProject: () => dispatchUndoableOperation("Projects", { type: "ADD_PROJECT" }),
    handleDeleteProject: (id) => dispatchUndoableOperation("Projects", { type: "DELETE_PROJECT", value: id }),
    handleAddSkill: (skill) => dispatchUndoableOperation("Skills", { type: "ADD_SKILL", value: skill }),
    handleAddBullet: (section, companyName) => dispatchUndoableOperation(section, { type: "ADD_BULLET", section, companyName }),
    setIsStreaming: session.setIsStreaming,
    setStreamingText: session.setStreamingText,
    setCreditsRetryCount,
    isPanningRef: canvas.isPanningRef,
    SUGGESTION_CHIPS,
    SAMPLE_RESUME_DATA,
    // ── Import Modal / Review Screen ──
    showImportModal,
    setShowImportModal,
    importMode,
    importReviewData,
    handleImportAccept,
    handleImportEdit,
    handleImportDiscard,
    triggerImportModal,
    handleImportModalConfirm,
    hasActiveConversation: (session.conversation || []).length > 0,
    // ── Autosave Status ──
    autosaveStatus,
    setAutosaveStatus,
    // ── Creation UI State (UI-only, not routing logic) ──
    isCreatingDocument,
    setIsCreatingDocument,
    // ── AI Tone Preference ──
    aiTone,
    setAiTone,
    // ── AI Observability Debugger ──
    aiDebugEnabled,
    setAiDebugEnabled,
    pipelineEvents: session.pipelineEvents,
    setPipelineEvents: session.setPipelineEvents,
    latestDebugPayload: session.latestDebugPayload,
    setLatestDebugPayload: session.setLatestDebugPayload,
  };


  return (
    <AppContext.Provider value={ctxValue}>
      {children}
    </AppContext.Provider>
  );
};
